<template>
  <div>
    <div class="tab">
      <div class="tab__header">
        <div class="tab__header-select">
          <Select2
            v-if="collectionsFormatted"
            v-model="currentCollectionId"
            :options="collectionsFormatted"
            :settings="{ minimumResultsForSearch: -1 }"
          />
        </div>

        <div class="tab__title h2">Марафон ПДД</div>
      </div>

      <div class="tab__content marathon-landing">
        <p class="marathon-landing__description">
          Все вопросы выбранной категории в случайном порядке.
          Вопросы, в которых вы ошибаетесь, будут возвращаться чаще,
          пока вы не ответите на каждый правильно несколько раз подряд.
        </p>

        <template v-if="savedSessions.length > 0">
          <div class="h3 marathon-landing__subtitle">Начатые марафоны</div>
          <p class="marathon-landing__sessions-note">
            Вы можете прерывать и возобновлять марафон в любой момент — прогресс сохраняется. Марафоны без активности в течение 3 дней удаляются автоматически.
          </p>
          <div class="marathon-sessions-list">
            <div
              v-for="s in savedSessions"
              :key="s.uuid"
              class="marathon-session-card"
            >
              <div class="marathon-session-card__info">
                <div class="marathon-session-card__name">{{ s.category_name }}</div>
                <div class="marathon-session-card__stats">
                  <span class="marathon-session-card__stat">
                    <span class="marathon-session-card__stat-label">Выучено</span>
                    <span class="marathon-session-card__stat-value">{{ s.mastered }} / {{ s.total }}</span>
                  </span>
                  <span class="marathon-session-card__stat">
                    <span class="marathon-session-card__stat-label">Охват</span>
                    <span class="marathon-session-card__stat-value">{{ s.seen_unique }} / {{ s.total }}</span>
                  </span>
                  <span class="marathon-session-card__stat">
                    <span class="marathon-session-card__stat-label">Ошибок</span>
                    <span class="marathon-session-card__stat-value">{{ s.wrong_last }}</span>
                  </span>
                  <span class="marathon-session-card__stat">
                    <span class="marathon-session-card__stat-label">Пройдено</span>
                    <span class="marathon-session-card__stat-value">{{ s.total_answered }}</span>
                  </span>
                </div>
                <div class="marathon-session-card__date">{{ formatDate(s.last_activity) }}</div>
              </div>
              <router-link
                :to="{ name: 'marathon-session', params: { uuid: s.uuid } }"
                class="btn btn-green marathon-session-card__btn"
              >Продолжить</router-link>
            </div>
          </div>
        </template>

        <div class="marathon-landing__actions">
          <button
            type="button"
            :class="['btn', savedSessions.length > 0 ? 'btn-blue' : 'btn-green']"
            :disabled="!currentCollectionId || isLoadingSession"
            @click="createMarathon"
          >
            {{ isLoadingSession ? 'Создание...' : (savedSessions.length > 0 ? 'Начать новый марафон' : 'Начать марафон') }}
          </button>
        </div>

        <p v-if="createError" class="marathon-landing__error">
          Не удалось создать марафон. Попробуйте ещё раз.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue';
  import { mapGetters, mapActions } from 'vuex';
  import Select2 from 'v-select2-component';
  import ls from '../../services/ls';
  import api from '../../services/index';

  Vue.component('Select2', Select2);

  const LS_COLLECTION_KEY = 'marathon_collection_id';
  const LS_SESSION_UUID_KEY = 'marathon-session-uuid';

  function buildSessionEntry(session) {
    const s = (session && session.state) || {};
    return {
      uuid: session.uuid,
      category_name: session.category_name || '',
      category_id: session.category_id || null,
      mastered: s.mastered_count || 0,
      total: s.total_questions || 0,
      seen_unique: s.seen_unique || 0,
      correct_last: s.correct_last || 0,
      wrong_last: s.wrong_last || 0,
      total_answered: s.total_answered || 0,
    };
  }

  export default {
    name: 'Marathon',
    data() {
      return {
        currentCollectionId: null,
        savedSessions: [],
        createError: false,
        collectionReady: false,
      };
    },
    computed: {
      ...mapGetters('marathon', { isLoadingSession: 'isLoadingSession' }),
      collections() {
        return this.$store.getters.collections;
      },
      collectionsFormatted() {
        if (this.collections && this.collections.length > 0) {
          return this.collections.map(({ name: text, ...rest }) => ({ text, ...rest }));
        }

        return null;
      },
    },
    methods: {
      ...mapActions('marathon', ['createSession']),
      resolveInitialCollectionId() {
        const fromQuery = this.$route.query.coll;
        if (fromQuery && this.collections.some(c => String(c.id) === String(fromQuery))) {
          return fromQuery;
        }

        const fromLs = localStorage.getItem(LS_COLLECTION_KEY);
        if (fromLs && this.collections.some(c => String(c.id) === String(fromLs))) {
          return fromLs;
        }

        return this.collections.length ? this.collections[0].id : null;
      },
      formatDate(iso) {
        if (!iso) return '';
        const d = new Date(iso);
        const now = new Date();
        const diffMs = now - d;
        const diffMin = Math.floor(diffMs / 60000);
        const diffH = Math.floor(diffMin / 60);
        const diffD = Math.floor(diffH / 24);

        if (diffMin < 1) return 'только что';
        if (diffMin < 60) return `${diffMin} мин. назад`;
        if (diffH < 24) return `${diffH} ч. назад`;
        if (diffD === 1) return 'вчера';
        if (diffD < 7) return `${diffD} дн. назад`;

        return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
      },
      validateSessions() {
        const sessions = ls.getMarathonSessions();
        if (!sessions.length) return;

        let changed = false;
        const checks = sessions.map(s => api.get(`/marathon/sessions/${s.uuid}`).catch((e) => {
          const status = e && e.response && e.response.status;
          if (status === 404) {
            ls.removeMarathonSession(s.uuid);
            changed = true;
          }
        }));

        Promise.all(checks).then(() => {
          if (changed) {
            this.savedSessions = ls.getMarathonSessions();
          }
        });
      },
      async createMarathon() {
        this.createError = false;

        try {
          const session = await this.createSession({
            category_id: parseInt(this.currentCollectionId, 10),
          });

          if (session && session.uuid) {
            const collection = this.collections.find(c => String(c.id) === String(this.currentCollectionId));
            ls.upsertMarathonSession({
              uuid: session.uuid,
              category_name: session.category_name || (collection ? collection.name : ''),
              category_id: session.category_id || parseInt(this.currentCollectionId, 10),
              mastered: 0,
              total: session.state ? (session.state.total_questions || 0) : 0,
              seen_unique: 0,
              correct_last: 0,
              wrong_last: 0,
              total_answered: 0,
            });
            this.$router.push({ name: 'marathon-session', params: { uuid: session.uuid } });
          } else {
            this.createError = true;
          }
        } catch (e) {
          this.createError = true;
        }
      },
    },
    created() {
      this.savedSessions = ls.getMarathonSessions();

      const oldUuid = ls.get(LS_SESSION_UUID_KEY);
      if (oldUuid && !this.savedSessions.some(s => s.uuid === oldUuid)) {
        api.get(`/marathon/sessions/${oldUuid}`)
          .then((resp) => {
            if (resp && resp.data) {
              ls.upsertMarathonSession(buildSessionEntry(resp.data));
              this.savedSessions = ls.getMarathonSessions();
            }
          })
          .catch(() => {});
      }

      this.validateSessions();

      this.$store.dispatch('getCollections')
        .then(() => {
          this.currentCollectionId = this.resolveInitialCollectionId();
          this.collectionReady = true;
        });
    },
    watch: {
      currentCollectionId(id) {
        if (!id || !this.collectionReady) return;

        localStorage.setItem(LS_COLLECTION_KEY, id);

        if (String(this.$route.query.coll) !== String(id)) {
          this.$router.replace({
            query: { ...this.$route.query, coll: id },
          }).catch(() => {});
        }
      },
    },
  };
</script>

<style scoped>
.marathon-landing {
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  padding-right: 26px;
}
.marathon-landing__description {
  margin-bottom: 24px;
  font-size: 15px;
  line-height: 1.5;
  width: 100%;
}
.marathon-landing__subtitle {
  margin-bottom: 12px;
  width: 100%;
}
.marathon-landing__sessions-note {
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.5;
  color: #6b7280;
  width: 100%;
}
.marathon-landing__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
  width: 100%;
}
.marathon-landing__error {
  margin-top: 16px;
  color: #c00;
}

.marathon-sessions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.marathon-session-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}
.marathon-session-card__info {
  flex: 1;
  min-width: 0;
}
.marathon-session-card__name {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 6px;
}
.marathon-session-card__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 6px;
}
.marathon-session-card__stat {
  display: flex;
  flex-direction: column;
  font-size: 12px;
}
.marathon-session-card__stat-label {
  color: #888;
  line-height: 1.2;
}
.marathon-session-card__stat-value {
  font-weight: 600;
  font-size: 14px;
  color: #222;
}
.marathon-session-card__date {
  font-size: 12px;
  color: #aaa;
}
.marathon-session-card__btn {
  flex-shrink: 0;
  white-space: nowrap;
}
</style>
